/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'
import './ChartsWidgetTree.css'
import Tree, { Orientation, PathFunction, PathFunctionOption, Point } from 'react-d3-tree'
import { MenuComponent } from '../../../assets/ts/components'
import { getColorCodeFromDivision, getEmojiFromDivision } from '../../../helpers/utility'
import { LanguageHelp } from '../../../helpers/LanguageHelper'
import { Link } from 'react-router-dom'
import { KTSVG, toAbsoluteUrl } from '../../../helpers'

const SUBJECT_TREE_ROOT_NAMES = new Set([
  'Subjects',
  'Konular',
  'Themen',
  'الموضوعات',
  'Темы',
])

type Props = {
  id?: string
  className: string
  data: any
  texts?: string[]
  firstColor?: string
  secondColor?: string
  thirdColor?: string
  mainLabel?: string
  sideLabel?: string
  functionDownload?: any
  isDownloadContinue?: boolean
  options?: string[]
  warnings: { url: string, message: string }[]
  isWaringImageMirror?: boolean
  height?: string

  filter_Date: string
  filter_ChannelType: string
  filter_SourceType: string
  filter_Competitor: string

  orientation?: Orientation | undefined
  dimensions?: {
    width: number;
    height: number;
  } | undefined
  centeringTransitionDuration?: number | undefined
  translate?: Point | undefined
  pathFunc?: PathFunctionOption | PathFunction | undefined
  collapsible?: boolean
  initialDepth?: number | undefined
  zoomable?: boolean
  draggable?: boolean
  zoom?: number | undefined
  scaleExtent?: {
    min?: number;
    max?: number;
  } | undefined
  nodeSize?: {
    x: number;
    y: number;
  } | undefined
  separation?: {
    siblings?: number;
    nonSiblings?: number;
  } | undefined
  enableLegacyTransitions?: boolean | undefined
  transitionDuration?: number | undefined
  depthFactor?: number | undefined
  shouldCollapseNeighborNodes?: number | boolean
}

function getColor(color: string) {
  return "#" + color;
}

export class ChartsWidgetTreeDto {
  id = '';
  name = '';
  children = new Array();
  attributes = new AttributesDto();

  constructor(
    id: string = "",
    name: string = "",
    attributes: AttributesDto = new AttributesDto(),
    children: ChartsWidgetTreeDto[] = new Array()
  ) {
    this.id = id;
    this.name = name;
    this.attributes = attributes;
    this.children = children;
  }
}

export class AttributesDto {
  totalCount = 0;
  positiveCount = 0;
  negativeCount = 0;
  neutralCount = 0;
  happinessScore = 0;

  constructor(
    totalCount: number = 0,
    positiveCount: number = 0,
    negativeCount: number = 0,
    neutralCount: number = 0,
    happinessScore: any = 0,
  ) {
    this.totalCount = totalCount;
    this.positiveCount = positiveCount;
    this.negativeCount = negativeCount;
    this.neutralCount = neutralCount;
    this.happinessScore = happinessScore;
  }
}

const ChartsWidgetTree: React.FC<Props> = ({
  id = '',
  className,
  data,
  texts = ["Net Profit", "Net Profit"],
  firstColor = "47be7d",
  secondColor = "f1416c",
  thirdColor = "e4e6ef",
  mainLabel = "Your Statistics",
  sideLabel = "",
  functionDownload,
  isDownloadContinue = false,
  options = [],
  warnings = [],
  isWaringImageMirror = false,
  height = "250px",

  filter_Date = "",
  filter_ChannelType = "",
  filter_SourceType = "",
  filter_Competitor = "",

  orientation = 'horizontal',
  // orientation = 'vertical',
  dimensions = { width: 100, height: 100 },
  centeringTransitionDuration = 100,
  translate = { x: 100, y: 200 },
  // pathFunc='',
  collapsible = true,
  initialDepth = 3,
  zoomable = true,
  draggable = true,
  zoom = 1,
  scaleExtent = { min: 1, max: 100 },
  nodeSize = { x: 300, y: 30 },
  separation = { siblings: 1, nonSiblings: 1 },
  enableLegacyTransitions = true,
  transitionDuration = 300,
  depthFactor = 100,
  // styles='{Treestyles}',
  shouldCollapseNeighborNodes = true,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()
  // const [countChart, updateCountChart] = React.useState(0);
  const [previusData, updatePreviusData] = React.useState([new ChartsWidgetTreeDto()]);
  const [isChartCreated, updateIsChartCreated] = React.useState(false);
  const [chartId, updateChartId] = React.useState('id');

  const treeRef = useRef<HTMLDivElement>(null);
  const [translateInner, setTranslateInner] = useState<Point>(translate);
  const nodeRefs = useRef<Record<string, SVGGElement | null>>({});

  // https://github.com/bkrem/react-d3-tree?tab=readme-ov-file#styling-links    // renderCustomNodeElement
  const renderNodeWithCustomEvents = ({
    nodeDatum,
    toggleNode,
    handleNodeClick
  }: any) => (
    <g
      data-id={nodeDatum.id}
      ref={(el) => { if (el) nodeRefs.current[nodeDatum.id] = el; }}
    >
      <circle r="15" // className={nodeDatum.children ? "node__branch": "rd3t-leaf-node"} 
      />
      <a href={'/apps/artificial-intelligence/overview?filter_subject=' + nodeDatum.id + '&filter_date=' + filter_Date + '&filter_channeltype=' + filter_ChannelType + '&filter_sourcetype=' + filter_SourceType + '&filter_competitor=' + filter_Competitor} className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary'>
        <text strokeWidth="0" x="20" onClick={toggleNode}
          className='fw-semibold fs-7'
          fill={getColorCodeFromDivision(nodeDatum.attributes?.positiveCount, nodeDatum.attributes?.negativeCount)}
        // style={{color: getColorCodeFromDivision(nodeDatum.attributes?.positiveCount, nodeDatum.attributes?.negativeCount)}}
        >
          {/* {nodeDatum.name} (Mutluluk: %{Math.trunc(nodeDatum.attributes?.happinessScore * 100)} - Adet: {nodeDatum.attributes?.totalCount}) */}
          {/* {nodeDatum.name} (😊: %{Math.trunc(nodeDatum.attributes?.happinessScore * 100)} - Adet: {nodeDatum.attributes?.totalCount}) */}
          {/* {nodeDatum.name} ({nodeDatum.attributes?.totalCount}, 😊: {Math.trunc(nodeDatum.attributes?.happinessScore * 100) /100}) */}
          {/* {nodeDatum.name} ({nodeDatum.attributes?.totalCount}, {getEmojiFromDivision(nodeDatum.attributes?.positiveCount, nodeDatum.attributes?.negativeCount)}: %{Math.trunc((nodeDatum.attributes?.happinessScore == 0 ? 0.5 : nodeDatum.attributes?.happinessScore) * 100)}) */}
          {nodeDatum.name} ({nodeDatum.attributes?.totalCount}, {getEmojiFromDivision(nodeDatum.attributes?.positiveCount, nodeDatum.attributes?.negativeCount)}: %{Math.trunc((nodeDatum.attributes?.happinessScore == 0 && nodeDatum.attributes?.negativeCount == 0 ? 0.5 : nodeDatum.attributes?.happinessScore) * 100)})
        </text>
      </a>
      {nodeDatum.attributes?.department && (
        <text x="20" dy="20" strokeWidth="0"
          className='fw-semibold fs-7'
          fill={getColorCodeFromDivision(nodeDatum.attributes?.positiveCount, nodeDatum.attributes?.negativeCount)}
        // style={{color: getColorCodeFromDivision(nodeDatum.attributes?.positiveCount, nodeDatum.attributes?.negativeCount)}}
        >
        </text>
      )}
    </g>
  );
  const handleNodeClick = (nodeDatum: any) => {
    // window.alert(
    //   nodeDatum.children ? "Clicked a branch node" : "Clicked a leaf node."
    // );
  };

  useEffect(() => {
    refreshChart()
    MenuComponent.reinitialization();
  })

  const refreshChart = () => {
    if (!chartRef.current) {
      // if (data![0].name === "") {
      updatePreviusData(data)
      // }
      return
    }

    if (isChartCreated) {
      if (JSON.stringify(previusData) === JSON.stringify(data)) {
        return
      }
      updatePreviusData(data)
    }

    var element = document.getElementById("apexcharts" + chartId);
    if (typeof (element) != 'undefined' && element != null) {

      ApexCharts.exec(chartId, 'updateOptions', options, true, true);
    } else {
      const chartId = "id" + Math.random().toString(16).slice(2)
      updateChartId(chartId)

      const chart = new ApexCharts(chartRef.current, options)
      chart.render()

      updateIsChartCreated(true)
    }
  }

  function findHighestChild(node: ChartsWidgetTreeDto): ChartsWidgetTreeDto {
    let current = node;
    let depth = 0;
    const maxDepth = 2;

    while (current.children && current.children.length > 0 && depth < maxDepth) {
      let bestChild = current.children[0];
      for (const child of current.children) {
        if (child.attributes.totalCount > bestChild.attributes.totalCount) {
          bestChild = child;
        }
      }
      current = bestChild;
      depth++;
    }
    return current;
  }
  
  useEffect(() => {
    setTranslateInner({ x: dimensions.width / 2, y: dimensions.height / 2 });
  }, [filter_Date, filter_ChannelType, filter_SourceType, filter_Competitor]);

  useLayoutEffect(() => {
    // API root label is localized (Subjects / Konular / Themen / …)
    if (
      !data ||
      data.length === 0 ||
      (!SUBJECT_TREE_ROOT_NAMES.has(data[0].name) && data[0].name !== LanguageHelp('Subjects'))
    )
      return;

    const targetNode = findHighestChild(data[0]);

    requestAnimationFrame(() => {
      const nodeEl = nodeRefs.current[targetNode.id];
      const container = treeRef.current;
      if (nodeEl && container) {
        const rect = nodeEl.getBoundingClientRect();
        const svgRect = container.getBoundingClientRect();

        setTranslateInner({
          x: svgRect.width / 2 - (rect.left - svgRect.left + rect.width / 2),
          y: svgRect.height / 2 - (rect.top - svgRect.top + rect.height / 2),
        });
      }
    });
  }, [data, filter_Date, filter_ChannelType, filter_SourceType, filter_Competitor]);

  return (
    <div className={`card ${className} minh-md-600px`} key={id}>
      <div className='card-header pt-5'>
        <div className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold text-dark'>{mainLabel}</span>
          {sideLabel !== "" &&
            <div>
              <span className='text-gray-400 fw-semibold fs-6'>{sideLabel}:</span>
              {data![0].attributes.totalCount !== 0 &&
                <div style={{ display: 'unset' }}>
                  <span className='card-label fw-bolder text-gray-700 mt-2 pt-1'>{data![0].attributes.totalCount}</span>
                </div>
              }
            </div>
          }
        </div>
        <div className='card-toolbar'>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary mb-2'
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-end'
            data-kt-menu-flip='top-end'
            onClick={(e) => { functionDownload() }}
            disabled={isDownloadContinue}
          >
            {!isDownloadContinue && (
              <KTSVG
                path='/media/icons/duotune/arrows/arr044.svg'
                className='svg-icon-3'
              />
            )}
            {isDownloadContinue && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
      {data![0].attributes.totalCount === -1 &&
        <div className='card-body pt-2 pb-4 d-flex flex-wrap align-items-center'>
          <div className='d-flex flex-center me-5 pt-2'>
            <span className='indicator-progress' style={{ display: 'block', margin: 'auto' }}>
              {/* Please wait...{' '} */}
              <span className='spinner-border spinner-border-lg align-middle ms-2'></span>
              <div
                style={{ minWidth: height, minHeight: height }}
              ></div>
            </span>
          </div>
        </div>
      }
      {(data![0].attributes.totalCount === 0) && (
        <div className='text-center p-11'>
          {warnings.map((warning, key) => (
            <Link key={key} to={warning.url}>
              <div>
                {warning.message}
                &nbsp;<KTSVG path='/media/icons/duotune/arrows/arr095.svg' className='svg-icon-2' />
              </div>
            </Link>
          ))}
          <div>
            <img
              className='mw-100 mh-200px'
              alt='metronic'
              style={{ transform: isWaringImageMirror ? 'scaleX(-1)' : 'scaleX(1)' }}
              src={toAbsoluteUrl('/media/illustrations/sketchy-1/1.png')}
            />
          </div>
        </div>
      )}

      {/* begin::Body */}
      {data![0].attributes.totalCount !== -1 && data![0].attributes.totalCount !== 0 && (
        <div className='card-body py-3' ref={treeRef}>
          {/* begin::Chart */}
          {/* <div ref={chartRef} id='kt_charts_widget_1_chart' style={{ height: height }} /> */}
          <div id="treeWrapper" style={{ height: '50em' }}>
            <Tree
              key={`${filter_Date}-${filter_ChannelType}-${filter_SourceType}-${filter_Competitor}`}
              data={data}
              pathClassFunc={() => 'custom-link'}
              hasInteractiveNodes={true}
              // renderCustomNodeElement={
              //   TreerenderCustomNodeElement
              //     ? rd3tProps => TreerenderCustomNodeElement(rd3tProps, this.state)
              //     : undefined
              // }
              rootNodeClassName="node__root"
              branchNodeClassName="node__branch"
              leafNodeClassName="node__leaf2"
              orientation={orientation}
              // renderCustomNodeElement={(rd3tProps) =>
              //   renderForeignObjectNode({ ...rd3tProps, foreignObjectProps })
              // }
              // renderCustomNodeElement={renderRectSvgNode}
              renderCustomNodeElement={(rd3tProps) =>
                renderNodeWithCustomEvents({ ...rd3tProps, handleNodeClick })
              }
              // dimensions={dimensions}
              // centeringTransitionDuration={centeringTransitionDuration}
              translate={translateInner}
              // pathFunc={TreepathFunc}
              collapsible={collapsible}
              initialDepth={initialDepth}
              zoomable={zoomable}
              draggable={draggable}
              zoom={zoom}
              // scaleExtent={scaleExtent}
              nodeSize={nodeSize}
              // separation={separation}
              // enableLegacyTransitions={enableLegacyTransitions}
              // transitionDuration={transitionDuration}
              // depthFactor={depthFactor}
              // styles={Treestyles}
              // shouldCollapseNeighborNodes={shouldCollapseNeighborNodes}
              // onUpdate={(...args) => { 
              //   console.log(args) 
              // }}
              // onNodeClick={(node, evt) => {
              //   console.log('onNodeClick', node, evt);
              // }}
              // onNodeMouseOver={(...args) => {
              //   console.log('onNodeMouseOver', args);
              // }}
              // onNodeMouseOut={(...args) => {
              //   console.log('onNodeMouseOut', args);
              // }}
              onLinkClick={(...args) => {
                console.log('onLinkClick');
                console.log(args);
              }}
            // onLinkMouseOver={(...args) => {
            //   console.log('onLinkMouseOver', args);
            // }}
            // onLinkMouseOut={(...args) => {
            //   console.log('onLinkMouseOut', args);
            // }}
            />
          </div>
          {/* end::Chart */}
        </div>
      )}
      {/* end::Body */}
    </div>
  )
}

export { ChartsWidgetTree }